import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    useToastProvider
} from '@deere/fuel-react';
import {useAuthConfig} from "../shared/helper";
import {useHistory, useParams} from 'react-router-dom';
import "../assets/ListItem.scss";
import {Loading} from "../shared/Loading";
import {useOktaAuth} from "@okta/okta-react";
import {SUPPLIER_MAINT_INIT_STATE} from "../shared/Constants";
import {useForm} from "react-hook-form";
import {SupplierUnitsCard} from "./SupplierUnitsCard";
import {DataGrid} from "@mui/x-data-grid";
import {SupplierQbicCard} from "./SupplierQbicCard";
import {SupplierSbmCard} from "./SupplierSbmCard";
import {SupplierProfileCard} from "./SupplierProfileCard";
import {SupplierInfoCard} from "./SupplierInfoCard";

export const SupplierCreateEdit = ({name}) => {
    let history = useHistory();
    let authAxios = useAuthConfig();
    const {authState} = useOktaAuth();
    const {enqueueToast} = useToastProvider();
    let {supplierNum} = useParams();
    const {control, handleSubmit} = useForm();
    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const [errorDesc, setErrorDesc] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [supplierData, setSupplierData] = useState(SUPPLIER_MAINT_INIT_STATE);
    const [subFunctionalAreas, setSubFunctionalAreas] = useState([]);
    const [alertVariant, setAlertVariant] = useState("warning");
    const [isSupplierNumberEmpty, setIsSupplierNumberEmpty] = useState(false);
    const [isStatusEmpty, setIsStatusEmpty] = useState(false);
    const [isCreateDataLoading, setIsCreateDataLoading] = useState(false);
    const [auditLog, setAuditLog] = useState([]);
    const [isFunctionalAreaError, setIsFunctionalAreaError] = useState(false);
    const [functionalAreaAlertVariant, setFunctionalAreaAlertVariant] = useState("warning");
    const [functionalAreaErrorDesc, setFunctionalAreaErrorDesc] = useState("");

    const getSupplierDetails = async (supplierNumber, type) => {
        try {
            return await authAxios.get(`/supplierMnt/${supplierNumber}/${type}`);
        } catch (e) {
            setIsCreateDataLoading(false)
            enqueueToast({
                variant: "error",
                title: "System Error",
                message: e.response ? e.response.data[e.response.status.toString()] : "",
                isPersistent: true
            });
            setTimeout(() => {
                history.replace(`/supplier-mnt/edit-ae-supplier/${supplierNumber}`);
            }, 2000);
        }
    };

    const onCancel = () => {
        history.push("/supplier-mnt");
    }

    const onSubfunctionalAreaChange = (value) => {
        setSupplierData({
            ...supplierData,
            subFunctionalAreaId: value
        });
    }

    const handleOption = (value) => {
        setIsStatusEmpty(false);
        setSupplierData({
            ...supplierData,
            supplierInd: value
        });
    }

    const handleSupplierChange = (value) => {
        setIsSupplierNumberEmpty(false)
        setErrorMsg("")
        setErrorDesc("")
        setSupplierData(SUPPLIER_MAINT_INIT_STATE)
        setIsCreateDataLoading(true)
        value && getSupplierDetails(value.supplierNumber, "create").then(res => {
            setIsCreateDataLoading(false)
            if (!res) {
                setIsDisabled(true)
            } else {
                setIsDisabled(false)
                if (!res.data.functionalAreaType) {
                    setErrorMsg("No SRO record found")
                    setErrorDesc("Please update SRO before creating AE supplier record")
                } else {
                    if (!res.data.functionalAreaObject)
                        setErrorMsg(`Functional Area ${res.data.functionalAreaType} is not available in AE.`)
                    else
                        setSubFunctionalAreas(res.data.functionalAreaObject.subFunctionalArea)
                    setSupplierData(res.data)
                }
            }
        });
    }

    const validateFormData = () => {
        const isSubFunctionalAreaValid = supplierData.subFunctionalAreaId !== "";
        const isSupplierNumberValid = supplierData.supplierNumber !== "";
        const isSupplierStatusValid = supplierData.supplierInd !== null;
        if (!isSupplierNumberValid) {
            setIsSupplierNumberEmpty(true);
            return false;
        }
        if (!isSupplierStatusValid) {
            setIsStatusEmpty(true);
            return false;
        }
        return isSubFunctionalAreaValid && isSupplierNumberValid && isSupplierStatusValid;
    }

    const processSupplierData = (data) => {
        const isSubFuncValid = data.functionalAreaObject && data.functionalAreaObject.subFunctionalArea.findIndex(ele => ele.id === data.subFunctionalAreaId)

        if (data.isFunctionalAreaSROMatched === false) {
            setIsFunctionalAreaError(true)
            setFunctionalAreaAlertVariant("warning")
            if (!data.sroFunctionalAreaType)
                setFunctionalAreaErrorDesc(`No SRO record found. Please update SRO before updating AE supplier record`)
            else
                setFunctionalAreaErrorDesc(`Functional area from SRO (${data.sroFunctionalAreaType}) is different from AE supplier functional Area.`)
        }

        if (isSubFuncValid === -1) {
            setAlertVariant("warning")
            setErrorMsg("Existing Sub Functional Area does not match the functional Area's Sub Functional Area Options")
            setErrorDesc("")
            setSupplierData({
                ...data,
                subFunctionalAreaId: ""
            });
        } else {
            setSupplierData(data)
        }

        setAuditLog([{
            "id": 1,
            "date": "DD-mmm-YYYY",
            "user": "lastname, Firstname",
            "field": "Supplier Created",
            "reason": "Requested By SBM"
        }, {
            "id": 2,
            "date": "DD-mmm-YYYY",
            "user": "lastname, Firstname",
            "field": "Supplier Created",
            "reason": "Requested By SBM"
        }])
        setSubFunctionalAreas(data.functionalAreaObject && data.functionalAreaObject.subFunctionalArea)
        setIsDisabled(false);
        setIsLoading(false);
    }
    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            hide: true
        },
        {
            field: 'date',
            headerName: 'Date',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" fontWeight="semibold">
                        {params.row.date}
                    </Typography>
                )
            }
        },
        {
            field: 'user',
            headerName: 'User',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" fontWeight="semibold">
                        {params.row.user}
                    </Typography>
                )
            }
        },
        {
            field: 'field',
            headerName: 'Field Changed',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" fontWeight="semibold">
                        {params.row.field}
                    </Typography>
                )
            }
        },
        {
            field: 'reason',
            headerName: 'Reason',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" fontWeight="semibold">
                        {params.row.reason}
                    </Typography>
                )
            }
        }
    ];

    const rowsData = auditLog && auditLog.map((item) => {
        return {
            id: item.id,
            date: item.date,
            user: item.user,
            field: item.field,
            reason: item.reason
        };
    });

    const onSubmit = async (type) => {
        const isValid = validateFormData();
        const data = {
            supplierNumber: supplierData.supplierNumber,
            functionalAreaId: supplierData.functionalAreaObject['id'],
            subFunctionalAreaId: supplierData.subFunctionalAreaId,
            supplierInd: supplierData.supplierInd
        }
        isValid && type === "submit" && await authAxios.post('/supplierMnt', data)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Supplier record created",
                    isCloseable: true
                });
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
                console.log(error);
            });

        isValid && type === "save" && await authAxios.put(`/supplierMnt/${supplierData.supplierNumber}`, data)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Supplier record updated",
                    isCloseable: true
                });
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
                console.log(error);
            });
    }

    useEffect(() => {
        setIsLoading(true);
        if (supplierNum) {
            getSupplierDetails(supplierNum, "edit").then(res => {
                if (!res || !res.data.functionalAreaType) {
                    setAlertVariant("warning")
                    setErrorMsg("No SRO record found")
                    setErrorDesc("Please update SRO before updating AE supplier record")
                    setIsDisabled(true);
                    setIsLoading(false);
                } else
                    processSupplierData(res.data)
            })
        }
        setIsLoading(false);
        return () => {
            setIsFunctionalAreaError(false)
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState])

    return (
        <form onSubmit={handleSubmit(() => onSubmit("submit"))}>
            {isLoading ? (
                <Loading/>
            ) : (
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12} md={8} className="sticky-header" style={{height: 50}}>
                        <>
                            <Typography variant="h1" component="h1" display="inline">
                                {name}
                            </Typography>
                            {supplierNum && (
                                <Typography variant="h1" component="h1" display="inline">
                                    {`- ${parseInt(supplierNum)}`}
                                </Typography>
                            )}
                        </>
                    </Grid>
                    <Grid item xs={12} md={4} className="sticky-header" style={{height: 60}}>
                        <Box display="flex" justifyContent="flex-end">
                            <Button variant="secondary" style={{marginRight: "1rem"}} size="large" onClick={() => {
                                onCancel()
                            }}>Cancel</Button>
                            {supplierNum ? (
                                <Button variant="primary" style={{marginRight: "1rem"}} size="large" type="submit"
                                        disabled={isDisabled}
                                        onClick={() => onSubmit("save")}
                                >Save</Button>) : (
                                <Button variant="primary" style={{marginRight: "1rem"}} size="large" type="submit"
                                        disabled={isDisabled}
                                        onClick={() => onSubmit("submit")}
                                >Submit</Button>)
                            }
                        </Box>
                    </Grid>
                    {isFunctionalAreaError && (
                        <Grid item xs={12}>
                            <Box pt={0} mb={2}>
                                <Alert variant={functionalAreaAlertVariant}>
                                    {functionalAreaErrorDesc && functionalAreaErrorDesc}
                                </Alert>
                            </Box>
                        </Grid>
                    )}
                    {errorMsg !== "" && (
                        <Grid item xs={12}>
                            <Box pt={0}>
                                <Alert variant={alertVariant} title={errorMsg}>
                                    {errorDesc && errorDesc}
                                </Alert>
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <Grid container alignItems="flex-start" spacing={2}>
                            {supplierNum ? (
                                <>
                                    {(supplierData.supplierNumber !== "" || errorMsg !== "") ? (
                                        <SupplierInfoCard
                                            supplierData={supplierData}
                                            supplierNum={supplierNum}
                                            control={control}
                                            isDisabled={isDisabled}
                                            errorMsg={errorMsg}
                                            subFunctionalAreas={subFunctionalAreas}
                                            onSubfunctionalAreaChange={onSubfunctionalAreaChange}
                                            onOptionsChange={handleSupplierChange}
                                            isSupplierNumberEmpty={isSupplierNumberEmpty}
                                        />
                                    ) : (
                                        <Grid item xs={12}>
                                            <Loading/>
                                        </Grid>
                                    )}
                                </>
                            ) : (
                                <SupplierInfoCard
                                    supplierData={supplierData}
                                    supplierNum={supplierNum}
                                    control={control}
                                    isDisabled={isDisabled}
                                    errorMsg={errorMsg}
                                    subFunctionalAreas={subFunctionalAreas}
                                    onSubfunctionalAreaChange={onSubfunctionalAreaChange}
                                    onOptionsChange={handleSupplierChange}
                                    isSupplierNumberEmpty={isSupplierNumberEmpty}
                                />
                            )}
                            {(supplierData.supplierNumber !== "" || errorMsg !== "") && (
                                <SupplierProfileCard
                                    supplierData={supplierData}
                                    isStatusEmpty={isStatusEmpty}
                                    control={control}
                                    isDisabled={isDisabled}
                                    onOptionChange={handleOption}
                                />
                            )}
                        </Grid>
                    </Grid>
                    {(supplierData.supplierNumber !== "" || errorMsg !== "") && (
                        <Grid item xs={12} md={6}>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <SupplierSbmCard supplierData={supplierData} supplierNum={supplierNum}/>
                                {supplierData.functionalAreaType === "DIRECT" && (
                                    <SupplierQbicCard supplierData={supplierData}/>
                                )}
                            </Grid>
                        </Grid>
                    )}
                    {(supplierData.supplierNumber !== "" || errorMsg !== "") && (
                        <>
                            <Grid item xs={12}>
                                <SupplierUnitsCard
                                    supplierNumber={supplierData.supplierNumber}
                                    control={control}/>
                            </Grid>
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Box m={1}>
                                            <DataGrid
                                                rows={rowsData}
                                                columns={columns}
                                                hideFooter
                                                checkboxSelection={false}
                                                disableSelectionOnClick
                                                autoHeight
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </>
                    )}
                    {isCreateDataLoading && (
                        <Grid item xs={12}>
                            <Loading/>
                        </Grid>
                    )}
                </Grid>
            )}
        </form>
    );
}
